import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaSpinner,
  FaArrowLeft,
  FaGraduationCap,
  FaCode,
  FaArrowRight,
  FaBookOpen,
  FaBrain,
  FaFire,
  FaStar,
  FaClock,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import axios from "../utils/axiosConfig";
import toast from "react-hot-toast";
import Loader from "../components/common/Loader";
import { useFooter } from "../context/FooterContext";

const ExploreCourses = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { setShowFooter } = useFooter();
  const [domains, setDomains] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setShowFooter(false);
    fetchDomains();
    fetchRecommendations();
    checkDarkMode();
    return () => setShowFooter(true);
  }, [isAuthenticated]);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchDomains = async () => {
    try {
      const response = await axios.get("/api/roadmap/domains");
      setDomains(response.data.domains);
    } catch (error) {
      console.error("Fetch domains error:", error);
      toast.error("Failed to fetch courses");
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get("/api/roadmap/recommendations");
      setRecommendations(response.data.recommendations);
    } catch (error) {
      console.error("Fetch recommendations error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (domainId, domainName) => {
    setEnrolling(domainId);
    try {
      const response = await axios.post(`/api/roadmap/enroll/${domainId}`);
      if (response.data.success) {
        toast.success(`Enrolled in ${domainName}!`);
        fetchDomains();
        fetchRecommendations();
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Already enrolled in this course");
      } else {
        toast.error("Failed to enroll");
      }
    } finally {
      setEnrolling(null);
    }
  };

  // Filter domains based on search term
  const filteredDomains = domains.filter((domain) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      domain.name.toLowerCase().includes(searchLower) ||
      domain.description?.toLowerCase().includes(searchLower) ||
      domain.tags?.some((tag) => tag.toLowerCase().includes(searchLower))
    );
  });

  // Filter recommendations based on search term
  const filteredRecommendations = recommendations.filter((course) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      course.name.toLowerCase().includes(searchLower) ||
      course.description?.toLowerCase().includes(searchLower) ||
      course.tags?.some((tag) => tag.toLowerCase().includes(searchLower))
    );
  });

  const clearSearch = () => {
    setSearchTerm("");
  };

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  if (loading) return <Loader />;

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
          padding: "2rem",
          borderRadius: "24px",
          marginBottom: "2rem",
          color: "white",
        }}
      >
        <button
          onClick={() => navigate("/learning/my-learning")}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            color: "white",
            cursor: "pointer",
            marginBottom: "1rem",
            padding: "0.5rem 1rem",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <FaArrowLeft /> Back to My Learning
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          <FaBookOpen size={32} />
          <h1 style={{ fontSize: "1.8rem", margin: 0 }}>Explore Courses</h1>
        </div>
        <p style={{ opacity: 0.9 }}>
          Discover new learning paths based on your interests.
        </p>
      </motion.div>

      {/* Search Bar - Under Header, Above Cards */}
      <div
        style={{
          marginBottom: "2rem",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            background: getInputBackground(),
            border: `2px solid ${getBorderColor()}`,
            borderRadius: "50px",
            padding: "0.3rem 0.3rem 0.3rem 1rem",
            transition: "border-color 0.3s",
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "#8b5cf6")}
          onBlur={(e) => (e.currentTarget.style.borderColor = getBorderColor())}
        >
          <FaSearch style={{ color: getTextSecondary(), fontSize: "1rem" }} />
          <input
            type="text"
            placeholder="Search courses by name, description, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: "0.8rem 0.8rem",
              background: "transparent",
              border: "none",
              outline: "none",
              color: getTextColor(),
              fontSize: "0.9rem",
            }}
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: getTextSecondary(),
                padding: "0.3rem",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FaTimes size={14} />
            </button>
          )}
        </div>
        {searchTerm && (
          <div
            style={{
              background: isDarkMode ? "#2b2d31" : "#f0f0f0",
              padding: "0.5rem 1rem",
              borderRadius: "20px",
              fontSize: "0.8rem",
              color: getTextSecondary(),
            }}
          >
            Found {filteredRecommendations.length + filteredDomains.length}{" "}
            {filteredRecommendations.length + filteredDomains.length === 1
              ? "course"
              : "courses"}{" "}
            matching "{searchTerm}"
          </div>
        )}
      </div>

      {/* Recommended for You Section - Based on Interests */}
      {filteredRecommendations.length > 0 && (
        <>
          <h2
            style={{
              color: getTextColor(),
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <FaBrain color="#8b5cf6" /> Recommended for You
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: "1.5rem",
              marginBottom: "2rem",
            }}
          >
            {filteredRecommendations.map((course) => (
              <motion.div
                key={course._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                whileHover={{ y: -5 }}
                style={{
                  background: getCardBackground(),
                  borderRadius: "20px",
                  padding: "1.5rem",
                  border: `1px solid ${getBorderColor()}`,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    background: "#f59e0b",
                    color: "white",
                    padding: "0.2rem 0.5rem",
                    borderRadius: "20px",
                    fontSize: "0.6rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.2rem",
                  }}
                >
                  <FaStar size={8} /> Recommended
                </div>
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    background: `${course.color}20`,
                    borderRadius: "15px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "1rem",
                  }}
                >
                  <FaCode size={24} color={course.color} />
                </div>
                <h3 style={{ color: getTextColor(), marginBottom: "0.3rem" }}>
                  {course.name}
                </h3>
                <p
                  style={{
                    color: getTextSecondary(),
                    fontSize: "0.8rem",
                    marginBottom: "1rem",
                    minHeight: "50px",
                  }}
                >
                  {course.description?.substring(0, 80)}...
                </p>
                <div style={{ marginBottom: "0.5rem" }}>
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: getTextSecondary(),
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                    }}
                  >
                    <FaBookOpen size={10} /> {course.totalTopics} topics
                  </span>
                </div>
                <button
                  onClick={() => handleEnroll(course._id, course.name)}
                  disabled={enrolling === course._id}
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontWeight: "500",
                    opacity: enrolling === course._id ? 0.7 : 1,
                  }}
                >
                  {enrolling === course._id ? "Enrolling..." : "Enroll Now"}
                </button>
              </motion.div>
            ))}
          </div>
        </>
      )}

      {/* All Courses Section */}
      <h2
        style={{
          color: getTextColor(),
          marginBottom: "1rem",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
        }}
      >
        <FaFire color="#f59e0b" /> All Courses
      </h2>

      {/* No Results Message */}
      {searchTerm &&
        filteredDomains.length === 0 &&
        filteredRecommendations.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              background: getCardBackground(),
              borderRadius: "20px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <FaSearch size={48} color={getTextSecondary()} />
            <h3 style={{ color: getTextColor(), marginTop: "1rem" }}>
              No courses found
            </h3>
            <p style={{ color: getTextSecondary() }}>
              No courses match "{searchTerm}". Try a different search term.
            </p>
            <button
              onClick={clearSearch}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                background: "#8b5cf6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Clear Search
            </button>
          </div>
        )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "1.5rem",
        }}
      >
        {filteredDomains.length === 0 && !searchTerm ? (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              color: getTextSecondary(),
            }}
          >
            No courses available yet. Check back soon!
          </div>
        ) : (
          filteredDomains.map((domain) => (
            <motion.div
              key={domain._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              whileHover={{ y: -5 }}
              style={{
                background: getCardBackground(),
                borderRadius: "20px",
                padding: "1.5rem",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  background: `${domain.color}20`,
                  borderRadius: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1rem",
                }}
              >
                <FaCode size={28} color={domain.color} />
              </div>
              <h3 style={{ color: getTextColor(), marginBottom: "0.5rem" }}>
                {domain.name}
              </h3>
              <p
                style={{
                  color: getTextSecondary(),
                  fontSize: "0.85rem",
                  marginBottom: "1rem",
                  minHeight: "60px",
                }}
              >
                {domain.description?.substring(0, 100)}...
              </p>

              <div style={{ marginBottom: "1rem" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "0.3rem",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: getTextSecondary(),
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                    }}
                  >
                    <FaBookOpen size={10} /> {domain.totalTopics} topics
                  </span>
                  {domain.isEnrolled && (
                    <span
                      style={{
                        fontSize: "0.7rem",
                        color: "#10b981",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.2rem",
                      }}
                    >
                      Enrolled
                    </span>
                  )}
                </div>

                {domain.isEnrolled ? (
                  <>
                    <div
                      style={{
                        height: "6px",
                        background: isDarkMode
                          ? "rgba(255,255,255,0.1)"
                          : "#e2e8f0",
                        borderRadius: "5px",
                        overflow: "hidden",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <div
                        style={{
                          width: `${domain.progress.percentage}%`,
                          height: "100%",
                          background: domain.color,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          color: getTextSecondary(),
                          fontSize: "0.7rem",
                        }}
                      >
                        {domain.progress.completed} / {domain.totalTopics}{" "}
                        completed
                      </span>
                      <span
                        style={{
                          color: domain.color,
                          fontSize: "0.7rem",
                          fontWeight: "500",
                        }}
                      >
                        {Math.round(domain.progress.percentage)}%
                      </span>
                    </div>
                    <button
                      onClick={() => navigate(`/learning/course/${domain._id}`)}
                      style={{
                        width: "100%",
                        marginTop: "1rem",
                        padding: "0.6rem",
                        background: "linear-gradient(135deg, #10b981, #059669)",
                        color: "white",
                        border: "none",
                        borderRadius: "10px",
                        cursor: "pointer",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                      }}
                    >
                      Continue Learning <FaArrowRight />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleEnroll(domain._id, domain.name)}
                    disabled={enrolling === domain._id}
                    style={{
                      width: "100%",
                      marginTop: "1rem",
                      padding: "0.6rem",
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      cursor: "pointer",
                      fontWeight: "500",
                      opacity: enrolling === domain._id ? 0.7 : 1,
                    }}
                  >
                    {enrolling === domain._id ? "Enrolling..." : "Enroll Now"}
                  </button>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ExploreCourses;
